import { captureException } from '@mentimeter/errors/sentry';
import { MentiError } from '@mentimeter/error-utils/error';
import type { QuestionSubType } from '@mentimeter/legacy-series-types/common';
import type { StaticContentWithVisualization } from '../../../compatibility-overrides/editor-types';

type Visualization =
  StaticContentWithVisualization['interactiveContentStyling']['visualization'];

const DEFAULT_RATING_GRID_VISUALIZATION: Visualization = 'two-values';

export const DEFAULT_RATING_GRID_IDS = [
  'blank',
  '2x2',
  '3x3',
  '4x4',
  'boxes',
  'frames',
  'boxes_top',
  'boxes_middle',
  'vertical',
  'bcg',
  'max-waves',
  'adl_matrix',
] as const;

type DefaultRatingGridId = (typeof DEFAULT_RATING_GRID_IDS)[number];

const UNAVAILABLE_RATING_GRID_SUBTYPE_TO_SLIDE_VISUALIZATION =
  DEFAULT_RATING_GRID_IDS.reduce(
    (acc, gridId) => ({
      [gridId]: DEFAULT_RATING_GRID_VISUALIZATION,
      ...acc,
    }),
    {} as Record<DefaultRatingGridId, Visualization>,
  );

export const UNAVAILABLE_SCALES_SUBTYPE_TO_SLIDE_VISUALIZATION = {
  default: 'slider-chart',
} satisfies Record<string, Visualization>;

export const UNAVAILABLE_MULTIPLE_CHOICE_SUBTYPE_TO_SLIDE_VISUALIZATION = {
  'single-image': 'bar_chart',
} satisfies Record<string, Visualization>;

export const UNAVAILABLE_SLIDE_TYPE_SUBTYPE_TO_SLIDE_VISUALIZATION = {
  'this-or-that': 'donut_chart',
} satisfies Record<string, Visualization>;

type LegacyQuestionSubType =
  | QuestionSubType
  | keyof typeof UNAVAILABLE_RATING_GRID_SUBTYPE_TO_SLIDE_VISUALIZATION
  | keyof typeof UNAVAILABLE_SCALES_SUBTYPE_TO_SLIDE_VISUALIZATION
  | keyof typeof UNAVAILABLE_MULTIPLE_CHOICE_SUBTYPE_TO_SLIDE_VISUALIZATION
  | keyof typeof UNAVAILABLE_SLIDE_TYPE_SUBTYPE_TO_SLIDE_VISUALIZATION;

export const QUESTION_SUBTYPE_TO_SLIDE_VISUALIZATION: Partial<
  Record<Exclude<LegacyQuestionSubType, null>, Visualization>
> = {
  ...UNAVAILABLE_RATING_GRID_SUBTYPE_TO_SLIDE_VISUALIZATION,
  ...UNAVAILABLE_SCALES_SUBTYPE_TO_SLIDE_VISUALIZATION,
  ...UNAVAILABLE_MULTIPLE_CHOICE_SUBTYPE_TO_SLIDE_VISUALIZATION,
  ...UNAVAILABLE_SLIDE_TYPE_SUBTYPE_TO_SLIDE_VISUALIZATION,
  dots: 'dots',
  choices: 'bar_chart',
  donut: 'donut_chart',
  pie: 'pie_chart',
  flow: 'flowing_grid',
  open: 'speech_bubbles',
  spider: 'spider-chart',
  scales: 'slider-chart',
  'two-values': 'two-values',
  'four-values': 'four-values',
};

export const questionSubTypeToSlideVisualization = (
  subType: LegacyQuestionSubType,
) => {
  if (!subType) {
    return null;
  }

  const visualization = QUESTION_SUBTYPE_TO_SLIDE_VISUALIZATION[subType];

  if (!visualization) {
    const error = new MentiError(
      `Unhandled subtype when converting subtype to visualization: \`${String(subType)}\``,
      {
        feature: 'compatibility-layer',
      },
    );
    captureException(error);

    return null;
  }

  return visualization;
};
